import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EyeIcon } from 'src/components/Icons/icons';
import { useConfirmationModalContext } from 'src/components/Modals/ConfirmationModal/ConfirmationModalProvider';
import { MessageComment } from 'src/domain/message';
import { UserRole } from 'src/domain/user';
import { LinkifiedText } from './LinkifiedText';

type MessageCommentItemProps = {
    comment: MessageComment;
    viewers: Array<{ userId: string; label: string }>;
    viewersLoaded: boolean;
    isLoadingViewers: boolean;
    onRequestViewers: () => void;
    canManage: boolean;
    isBusy?: boolean;
    onUpdate: (body: string) => Promise<void>;
    onDelete: () => Promise<void>;
};

export const MessageCommentItem = ({
    comment,
    viewers,
    viewersLoaded,
    isLoadingViewers,
    onRequestViewers,
    canManage,
    isBusy,
    onUpdate,
    onDelete,
}: MessageCommentItemProps) => {
    const { t } = useTranslation();
    const { showConfirmation } = useConfirmationModalContext();
    const [isEditing, setIsEditing] = useState(false);
    const [isViewersOpen, setIsViewersOpen] = useState(false);
    const [draft, setDraft] = useState(comment.body);

    const handleSave = async () => {
        const nextBody = draft.trim();
        if (!nextBody.length || nextBody === comment.body.trim()) {
            setIsEditing(false);
            setDraft(comment.body);
            return;
        }

        await onUpdate(nextBody);
        setIsEditing(false);
    };

    const handleDelete = async () => {
        const confirmed = await showConfirmation();
        if (!confirmed) return;
        await onDelete();
    };

    const isFromRelative = comment.authorRole === UserRole.nextOfKin;

    const openViewers = () => {
        setIsViewersOpen(true);
        onRequestViewers();
    };

    return (
        <div
            className={`relative rounded-lg px-3 py-2 ${
                isFromRelative ? 'bg-green-100' : 'bg-woodyBrown/5'
            }`}
        >
            <div className="flex justify-between items-center text-xs text-mongooseGray">
                <span>{comment.authorName || comment.authorId}</span>

                <div className="flex items-center gap-2">
                    <div
                        className="relative h-4 w-4"
                        onMouseEnter={openViewers}
                        onMouseLeave={() => setIsViewersOpen(false)}
                    >
                        <button
                            type="button"
                            className="inline-flex h-4 w-4 items-center text-mongooseGray hover:text-woodyBrown focus:outline-none focus:text-woodyBrown"
                            aria-label={t(
                                'modules.messages.commentsViewsButtonLabel',
                            )}
                            aria-expanded={isViewersOpen}
                            onClick={() => {
                                if (isViewersOpen) {
                                    setIsViewersOpen(false);
                                } else {
                                    openViewers();
                                }
                            }}
                        >
                            <span
                                className="relative h-4 w-4"
                                aria-hidden="true"
                            >
                                <EyeIcon />
                            </span>
                        </button>

                        {isViewersOpen && (
                            <div className="absolute right-0 top-full z-20 mt-1 min-w-[210px] rounded-lg border border-starkWhite bg-white p-2 text-left shadow-lg">
                                {isLoadingViewers || !viewersLoaded ? (
                                    <p className="text-xs text-mongooseGray">
                                        {t('common.loading')}
                                    </p>
                                ) : viewers.length ? (
                                    <ul className="max-h-36 overflow-y-auto text-xs text-mongooseGray">
                                        {viewers.map(viewer => (
                                            <li
                                                key={viewer.userId}
                                                className="py-0.5"
                                            >
                                                {viewer.label}
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="text-xs text-mongooseGray">
                                        {t('modules.messages.commentsNoViews')}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    <span>
                        {new Date(comment.createdDate).toLocaleString()}
                    </span>

                    {canManage && !isEditing && (
                        <>
                            <button
                                type="button"
                                className="text-woodyBrown underline disabled:opacity-60"
                                disabled={isBusy}
                                onClick={() => {
                                    setDraft(comment.body);
                                    setIsEditing(true);
                                }}
                            >
                                {t('modules.messages.commentsEdit')}
                            </button>
                            <button
                                type="button"
                                className="text-red-700 underline disabled:opacity-60"
                                disabled={isBusy}
                                onClick={handleDelete}
                            >
                                {t('common.delete')}
                            </button>
                        </>
                    )}
                </div>
            </div>

            {isEditing ? (
                <div className="mt-2 flex flex-col gap-2">
                    <textarea
                        className="text-input w-full min-h-[80px]"
                        value={draft}
                        onChange={event => setDraft(event.target.value)}
                    />
                    <div className="flex justify-end gap-2">
                        <button
                            type="button"
                            className="secondary-btn-small px-3 py-1.5"
                            onClick={() => {
                                setDraft(comment.body);
                                setIsEditing(false);
                            }}
                            disabled={isBusy}
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            type="button"
                            className="primary-btn-small px-3 py-1.5 disabled:opacity-60"
                            onClick={handleSave}
                            disabled={isBusy || !draft.trim().length}
                        >
                            {t('modules.messages.commentsEditSave')}
                        </button>
                    </div>
                </div>
            ) : (
                <LinkifiedText
                    as="div"
                    text={comment.body}
                    className="mt-1 text-sm whitespace-pre-wrap"
                />
            )}
        </div>
    );
};
